import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { getLeadIntentCopy, getSite } from '../content/index.js';
import { useLeadModal } from './LeadModalContext.js';
import {
  isValidEmail,
  isValidIndianMobile,
  normalizeIndianMobile,
  phoneFieldError
} from '../utils/leadValidation.js';

export default function LeadForm({
  title,
  subtitle,
  leadSource = 'hero_form',
  submitLabel,
  intent = 'counsel',
  defaultProgram = '',
  onClose,
  onSuccess,
  successMessage,
  consentText,
  extraPayload,
  keepOpenAfterSuccess = false
}) {
  const site = getSite();
  const intentCopy = getLeadIntentCopy(intent);
  const isFees = intent === 'fees';
  const { notifyLeadSubmitted } = useLeadModal();
  const [leadStatus, setLeadStatus] = useState(null);
  const [leadName, setLeadName] = useState('');
  const [leadPhone, setLeadPhone] = useState('');
  const [leadEmail, setLeadEmail] = useState('');
  const [preferredProgram, setPreferredProgram] = useState(defaultProgram);
  const [consent, setConsent] = useState(true);
  const [fieldErrors, setFieldErrors] = useState({});

  function handlePhoneChange(event) {
    const raw = event.target.value;
    const normalized = normalizeIndianMobile(raw);
    setLeadPhone(normalized);
    setFieldErrors((prev) => ({
      ...prev,
      phone: raw && (raw.includes('@') || (normalized.length === 10 && !isValidIndianMobile(normalized)))
        ? phoneFieldError(raw, normalized)
        : ''
    }));
  }

  function validate() {
    const errors = {};
    if (!leadName.trim()) {
      errors.name = 'Enter your full name.';
    }
    const phoneError = phoneFieldError(leadPhone, leadPhone);
    if (phoneError) {
      errors.phone = phoneError;
    }
    const email = leadEmail.trim();
    if (isFees && !email) {
      errors.email = 'Enter your email so we can send the fee details.';
    } else if (email && !isValidEmail(email)) {
      errors.email = 'Enter a valid email address.';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleLeadSubmit(event) {
    event.preventDefault();
    setLeadStatus(null);
    if (!validate()) {
      return;
    }

    try {
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: leadName.trim(),
          phone: leadPhone,
          email: leadEmail.trim() || undefined,
          consentMarketing: consent,
          leadSource,
          preferredProgram: preferredProgram.trim() || undefined,
          notes: isFees ? 'Intent: email course fees' : undefined,
          ...(extraPayload || {})
        })
      });

      if (!res.ok) {
        throw new Error('Lead creation failed');
      }

      let payload = null;
      try {
        payload = await res.json();
      } catch {
        payload = null;
      }

      setLeadStatus({
        type: 'success',
        message:
          successMessage ||
          intentCopy.success ||
          'Thanks! A counsellor will contact you shortly.',
        downloadToken: payload?.brochureToken || null
      });
      notifyLeadSubmitted({ keepOpen: keepOpenAfterSuccess });
      if (typeof onSuccess === 'function') {
        onSuccess(payload);
      }
      setLeadName('');
      setLeadPhone('');
      setLeadEmail('');
      setPreferredProgram(defaultProgram);
      setConsent(true);
      setFieldErrors({});
    } catch {
      setLeadStatus({
        type: 'error',
        message: 'Could not submit your details. Please try again.'
      });
    }
  }

  return (
    <div className={`lead-card${onClose ? ' lead-card-modal' : ''}`} id={onClose ? undefined : 'counsel'}>
      {onClose && (
        <button type="button" className="lead-modal-close" onClick={onClose} aria-label="Close form">
          ×
        </button>
      )}
      <h2>{title || intentCopy.title}</h2>
      {(subtitle || intentCopy.subtitle) && (
        <p className="lead-subtitle">{subtitle || intentCopy.subtitle}</p>
      )}
      <form className="lead-form" onSubmit={handleLeadSubmit} noValidate>
        <label className="field-label">
          Full name
          <input
            placeholder="Your full name"
            value={leadName}
            onChange={(e) => setLeadName(e.target.value)}
            required
            autoComplete="name"
            aria-invalid={Boolean(fieldErrors.name)}
            aria-describedby={fieldErrors.name ? `lead-name-error-${leadSource}` : undefined}
          />
          {fieldErrors.name && (
            <span className="field-error" id={`lead-name-error-${leadSource}`}>
              {fieldErrors.name}
            </span>
          )}
        </label>
        <label className="field-label">
          Mobile number
          <input
            placeholder="10-digit mobile number"
            value={leadPhone}
            onChange={handlePhoneChange}
            required
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            maxLength={10}
            pattern="[6-9][0-9]{9}"
            aria-invalid={Boolean(fieldErrors.phone)}
            aria-describedby={
              fieldErrors.phone ? `lead-phone-error-${leadSource}` : undefined
            }
          />
          {fieldErrors.phone && (
            <span className="field-error" id={`lead-phone-error-${leadSource}`}>
              {fieldErrors.phone}
            </span>
          )}
        </label>
        <label className="field-label">
          Email
          <input
            placeholder="you@example.com"
            type="email"
            value={leadEmail}
            onChange={(e) => {
              setLeadEmail(e.target.value);
              setFieldErrors((prev) => ({ ...prev, email: '' }));
            }}
            autoComplete="email"
            required={isFees}
            aria-invalid={Boolean(fieldErrors.email)}
            aria-describedby={fieldErrors.email ? `lead-email-error-${leadSource}` : undefined}
          />
          {isFees && intentCopy.emailHint && (
            <span className="field-hint">{intentCopy.emailHint}</span>
          )}
          {fieldErrors.email && (
            <span className="field-error" id={`lead-email-error-${leadSource}`}>
              {fieldErrors.email}
            </span>
          )}
        </label>
        <label className="field-label">
          Preferred program
          <input
            placeholder="e.g. Online MBA"
            value={preferredProgram}
            onChange={(e) => setPreferredProgram(e.target.value)}
          />
        </label>
        <div className="consent-label">
          <input
            id={`lead-consent-${leadSource}`}
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            required
          />
          <div>
            <label htmlFor={`lead-consent-${leadSource}`}>
              {consentText ||
                intentCopy.consent ||
                'I agree to be contacted about programs via phone, email, or WhatsApp.'}
            </label>{' '}
            See the <Link to="/privacy">Privacy Policy</Link> and{' '}
            <Link to="/terms">Terms of Use</Link>.
          </div>
        </div>
        <button type="submit">
          {submitLabel || intentCopy.submit || site.ctaSubmit}
        </button>
        {leadStatus && <p className={`status-text ${leadStatus.type}`}>{leadStatus.message}</p>}
        {leadStatus?.downloadToken && (
          <a
            className="cta-button brochure-download-link"
            href={`/api/brochures/${leadStatus.downloadToken}`}
            download
          >
            Download brochure again
          </a>
        )}
      </form>
    </div>
  );
}
